import { useMemo, useRef } from 'react';

/**
 * Any slice action set: a map of void-returning action creators. The `never[]` parameter
 * constraint accepts every concrete action signature (parameters are contravariant).
 */
type ActionSet = Record<string, (...args: never[]) => void>;

/**
 * Gates a `[]`-stable action set behind `areActionsEnabled`, checking the gate at CALL time.
 *
 * The returned functions are permanently identity-stable (memoized on `actions` alone, which every
 * slice provider builds with `[]` deps), so consumers may safely capture them once — in `[]`-dep
 * callbacks, effects, or refs — and still dispatch correctly after `areActionsEnabled` changes.
 * An identity-swap implementation (exposing a no-op mirror while disabled) breaks that contract:
 * a consumer that captured the set while the provider was disabled would hold dead no-ops forever
 * after AI enables, which is a real mount sequence (students mount disabled until courses load).
 *
 * While disabled, calling any gated action is a no-op — state stays readable, writes are
 * suppressed — preserving the AI-disabled contract the slice providers expose.
 */
export function useGatedActions<T extends ActionSet>(actions: T, areActionsEnabled: boolean): T {
  // Latest-value ref, synced during render so the gate is accurate within the same commit the
  // flag changes in (an effect-synced ref would lag one paint behind).
  const isEnabledRef = useRef(areActionsEnabled);
  isEnabledRef.current = areActionsEnabled;

  return useMemo(() => {
    const gated = {} as Record<string, (...args: unknown[]) => void>;
    (Object.keys(actions) as Array<keyof T & string>).forEach((key) => {
      gated[key] = (...args: unknown[]) => {
        if (isEnabledRef.current) {
          (actions[key] as (...forwardedArgs: unknown[]) => void)(...args);
        }
      };
    });
    return gated as T;
  }, [actions]);
}

export default useGatedActions;
